import React from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Target } from 'lucide-react'
import { formatNumber } from '../../../utils/formatters'

/**
 * Плашка личного статуса в эвенте — всегда над заданиями.
 * Либо «Осталось N заданий» + бар tasks_done/tasks_total,
 * либо «Ты в игре · место · шанс · баллы».
 * qualified умеет переключаться с true на false (откат при продаже предметов).
 */
const RushDropStatus = ({ you }) => {
  const { t } = useTranslation()

  if (!you) return null

  const tasksDone = Number(you.tasks_done || 0)
  const tasksTotal = Number(you.tasks_total || 0)
  const tasksLeft = Math.max(0, tasksTotal - tasksDone)
  const bonusPoints = Number(you.bonus_points || 0)
  const bonusCap = Number(you.bonus_cap || 0)
  const isBonusCapped = bonusCap > 0 && bonusPoints >= bonusCap

  if (you.qualified) {
    return (
      <div className="rounded-2xl border border-accent-primary/40 bg-accent-primary/10 p-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={18} className="shrink-0 text-accent-primary" />
          <span className="font-bold text-accent-primary">
            {t('rushdrop.status.inGame', 'Ты в игре')}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white">
          {you.rank != null && (
            <span>
              {t('rushdrop.status.placeByPoints', 'Место по баллам')}: <b className="tabular-nums">{you.rank}</b>
            </span>
          )}
          <span>
            {t('rushdrop.status.chance', 'Шанс')}:{' '}
            <b className="tabular-nums">{Number(you.chance_pct || 0).toLocaleString()}%</b>
          </span>
          <span>
            {t('rushdrop.status.points', 'Баллы')}:{' '}
            <b className="tabular-nums">{formatNumber(Number(you.points || 0))}</b>
          </span>
        </div>
        {isBonusCapped && (
          <p className="mt-1.5 text-xs text-accent-secondary">
            {t('rushdrop.status.bonusCapped', 'Максимум бонусных баллов набран')}
          </p>
        )}
      </div>
    )
  }

  const progressPercent = tasksTotal > 0 ? Math.min(100, Math.round((tasksDone / tasksTotal) * 100)) : 0

  return (
    <div className="rounded-2xl border border-white/10 bg-surface p-4">
      <div className="flex items-center gap-2">
        <Target size={18} className="shrink-0 text-accent-primary" />
        <span className="text-sm font-semibold text-white">
          {t('rushdrop.status.tasksLeft', { count: tasksLeft })}
        </span>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-gradient-to-r from-accent-primary to-accent-secondary transition-all duration-700"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-xs text-text-secondary">
        <span className="tabular-nums">{tasksDone} / {tasksTotal}</span>
        {bonusPoints > 0 && (
          <span className="text-accent-secondary">
            {t('rushdrop.status.bonusReady', { count: bonusPoints, formatted: formatNumber(bonusPoints) })}
          </span>
        )}
      </div>
    </div>
  )
}

export default RushDropStatus
